import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {t} from "i18next";

import {fetchData, fetchDataNew, fetchList, fetchListNew} from "../../../hook/axios.hook";
import {toaster} from "../../../utilities/hooks/toaster";

const initialState = {
  ownerTypes: [],
  types: [],
  classes: [],
  bsAccounts: [],
  currentBsAccount: null,
  accounts: [],
  currentAccount: null,
  transactionPackageGroups: [],
  deskOperations: [],
  allDirectives: [],
  directive: null,
  currentDirectiveOperationTypeId: null,
  statementDirective: null,
  directiveDoc: null,
  currentOperation: null,
  transactions: [],
  readyToCreateDirectiveCO: null,
  operationTypes: [],
  finOrderTypes: [],
  repaymentTerms: [],
  createdDirective: null,
  statuses: [],
  finOrder: null,
  filters: [],

  allCurrency: [],

  allExchangeRates: [],
  allEcbRates: [],
  currentRate: null,

  package: null,

  externalAccounts: [],
  externalAccountFilters: [],
  banks: [],
  banksLoading: false,
  companyAccounts: [],
  currentExternalAccount: null,

  counterpartyClasses: [],

  productGroups: [],
  productGroup: null,

  invoices: [],
  invoice: null,
  invoiceFiltersData: {},
  invoiceStatuses: [],
  invoiceRegisterFiltersData: {},

  registers: [],
  register: null,
  registerStatuses: [],

  daylistTransactions: [],
  daylistTransactionFilters: null,

  foreingBanks: [],
  foreingBanksFilters: {},

  allExchangeRatesLoadingStatus: false,
  allEcbRatesLoadingStatus: false,
  finSettingsLoading: false,
  error: false,

  companies: [],
  companiesLoading: false,
  currentCompany: null,
  companyFilters: {},
  companyTypes: [],
  companyStatuses: [],
};

//*** Operations ***

//** Balance Sheet Accounts **
export const fetchAllBsaTypes = createAsyncThunk("finSettings/fetchAllBsaTypes", async () =>
  fetchData("/finance/balanceSheet/type/getAll", "get"),
);

//** Banks and External Accounts (ForeignCompany) **
export const fetchBanks = createAsyncThunk("finSettings/fetchBanks", async () => {
  const data = await fetchData(`/finance/banks`, "get");
  return Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
});

export const fetchExternalAccount = createAsyncThunk("finSettings/fetchExternalAccount", async externalAccountId => {
  if (!externalAccountId) return null;
  const data = await fetchData(`/finance/externalAccounts/get/${externalAccountId}`, "get");
  return data?.data ?? data ?? null;
});

//** ForeignCompany **
export const fetchCompanies = createAsyncThunk("finSettings/fetchCompanies", async () => {
  const res = await fetchData(`/finance/companies`, "get");
  const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
  return list;
});
export const fetchCompany = createAsyncThunk("finSettings/fetchCompany", async companyId => {
  if (!companyId) return null;
  const data = await fetchData(`/finance/companies/${companyId}`, "get");
  return data?.data ?? data ?? null;
});
export const updateCompany = createAsyncThunk("finSettings/updateCompany", async ({companyId, body}) => {
  if (!companyId) return;
  return fetchData(`/finance/companies/${companyId}`, "patch", body);
});
export const deleteCompany = createAsyncThunk("finSettings/deleteCompany", async companyId => {
  if (!companyId) return;
  return fetchData(`/finance/companies/${companyId}`, "delete");
});
export const fetchCompanyFilters = createAsyncThunk("finSettings/fetchCompanyFilters", async () => {
  const data = await fetchData(`/finance/companies/getFilters`, "get");
  return data?.data ?? data ?? {};
});
export const fetchCompanyTypes = createAsyncThunk("finSettings/fetchCompanyTypes", async () => {
  return fetchList(`/finance/companies/type`, "get");
});
export const fetchCompanyStatuses = createAsyncThunk("finSettings/fetchCompanyStatuses", async () => {
  return fetchList(`/finance/companies/status`, "get");
});
export const createCompany = createAsyncThunk("finSettings/createCompany", async body => {
  return fetchData(`/finance/companies/`, "post", body, null, true, null, true);
});

export const createExternalAccount = createAsyncThunk(
  "finSettings/createExternalAccount",
  async ({companyId, body}, {rejectWithValue}) => {
    if (!companyId) return rejectWithValue("companyId is required");
    try {
      const res = await fetchData(`/finance/externalAccounts`, "post", body, null, false, {companyId});
      return res;
    } catch (err) {
      try {
        const res = await fetchData(`/finance/externalAccounts?companyId=${companyId}`, "post", body);
        return res;
      } catch (e) {
        return rejectWithValue(e?.message || "Failed to create external account");
      }
    }
  },
);

export const updateExternalAccount = createAsyncThunk(
  "finSettings/updateExternalAccount",
  async ({externalAccountId, body}) => {
    if (!externalAccountId) return;
    return fetchData(`/finance/externalAccounts/update/${externalAccountId}`, "patch", body);
  },
);

export const deleteExternalAccount = createAsyncThunk("finSettings/deleteExternalAccount", async externalAccountId => {
  if (!externalAccountId) return;
  return fetchData(`/finance/externalAccounts/remove/${externalAccountId}`, "delete");
});

export const fetchExternalAccountFilters = createAsyncThunk("finSettings/externalAccountFilters", async params => {
  return fetchDataNew("finance/externalAccounts/getFilters", "get", {params});
});
export const fetchAllBsaClasses = createAsyncThunk("finSettings/fetchAllBsaClasses", async () =>
  fetchData("/finance/balanceSheet/class/getAll", "get"),
);
export const fetchAllBsAccounts = createAsyncThunk("finSettings/fetchAllBsAccounts", async params =>
  fetchData("/finance/balanceSheet/getAll", "get", {params}),
);
export const fetchBsAccount = createAsyncThunk("finSettings/fetchAllBsAccount", async balanceSheetAccId => {
  if (!balanceSheetAccId) return;
  return fetchData(`/finance/balanceSheet/get/${balanceSheetAccId}`, "get");
});
export const addNewBsAccount = createAsyncThunk(
  "finSettings/addNewBsAccount",
  async ({balanceClassId, balanceTypeId, params}) => {
    if (!balanceClassId || !balanceTypeId) return;
    return fetchData(`/finance/balanceSheet/add/${balanceClassId}/${balanceTypeId}`, "post", {...params});
  },
);
export const updateBsAccount = createAsyncThunk("finSettings/updateBsAccount", async ({balanceSheetAccId, params}) => {
  if (!balanceSheetAccId) return;
  return fetchData(`/finance/balanceSheet/update/${balanceSheetAccId}`, "patch", {...params});
});
export const deleteBsAccount = createAsyncThunk("finSettings/deleteBsAccount", async balanceSheetAccId => {
  if (!balanceSheetAccId) return;
  return fetchData(`/finance/balanceSheet/remove/${balanceSheetAccId}`, "delete");
});
//** /Balance Sheet Accounts **

//** Accounts **
export const fetchAllAccounts = createAsyncThunk("finSettings/fetchAllAccounts", async params =>
  fetchData("/finance/accounts", "get", {params}),
);
export const fetchOwnerTypes = createAsyncThunk("finSettings/fetchOwnerTypes", async () =>
  fetchData("/finance/accountOwners", "get"),
);
export const fetchAccount = createAsyncThunk("finSettings/fetchAccount", async accountId => {
  if (!accountId) return;
  return fetchData(`/finance/accounts/${accountId}`, "get");
});
export const checkAccount = createAsyncThunk("finSettings/checkAccount", async accountId =>
  fetchData(`/finance/accounts/${accountId}`, "head"),
);
export const addNewAccount = createAsyncThunk("finSettings/addNewAccount", async (body, {rejectWithValue}) =>
  fetchDataNew(`/finance/accounts`, "post", {
    rawBody: {...body},
    rejectWithValue,
  }),
);
export const saveAccount = createAsyncThunk("finSettings/saveAccount", async accountId => {
  if (!accountId) return;
  return fetchData(`/finance/accounts/${accountId}`, "post");
});
export const updateAccount = createAsyncThunk("finSettings/updateAccount", async ({accountId}) => {
  if (!accountId) return;
  return fetchData(`/finance/accounts/${accountId}`, "patch");
});
export const deleteAccount = createAsyncThunk("finSettings/deleteAccount", async accountId => {
  if (!accountId) return;
  return fetchData(`/finance/accounts/${accountId}`, "delete");
});
//** /Accounts **

//*** /Operations ***

//*** Transaction templates ***
export const fetchTransactionPackageGroups = createAsyncThunk("finSettings/fetchTransactionPackageGroups", async () =>
  fetchData("/finance/transactionTemplates/packages/groups/getAll", "get"),
);
//*** /Transaction templates ***

//*** Desk Operations ***
export const fetchAllDeskOperations = createAsyncThunk("finSettings/fetchAllDeskOperations", async params =>
  fetchData("/finance/deskOperations/getAll", "get", {params}),
);
export const runOperation = createAsyncThunk("finSettings/runOperation", async ({operationId, params}) => {
  if (!operationId) return;
  return fetchData(`/finance/deskOperations/run/${operationId}`, "put", null, null, false, params);
});
export const fetchOperation = createAsyncThunk("finSettings/fetchOperation", async operationId => {
  if (!operationId) return;
  return fetchData(`/finance/deskOperations/get/${operationId}`, "get");
});
//*** /Desk Operations ***

//*** Directives ***
export const fetchAllDirectives = createAsyncThunk(
  "finSettings/fetchAllDirectives",
  async (params, {rejectWithValue, signal}) =>
    fetchDataNew("/finance/directives/getAll", "get", {params, rejectWithValue, signal}),
);
export const fetchDirective = createAsyncThunk("finSettings/fetchDirective", async ({directiveId}) => {
  if (!directiveId) return;
  return fetchData(`/finance/directives/get/${directiveId}`, "get");
});
export const fetchStatementDirective = createAsyncThunk(
  "finSettings/fetchStatementDirective",
  async ({directiveId}) => {
    if (!directiveId) return;
    return fetchData(`/finance/directives/get/${directiveId}`, "get");
  },
);
export const saveDirective = createAsyncThunk("finSettings/saveDirective", async ({directiveId, body}) => {
  if (!directiveId) return;
  return fetchData(`/finance/directives/save/${directiveId}`, "post", body);
});
export const addNewDirective = createAsyncThunk("finSettings/addNewDirective", async body =>
  fetchData(`/finance/directives/add`, "post", body),
);
export const updateDirective = createAsyncThunk("finSettings/updateDirective", async ({directiveId, body}) => {
  if (!directiveId) return;
  return fetchData(`/finance/directives/update/${directiveId}`, "patch", body);
});
export const cancelDirective = createAsyncThunk("finSettings/cancelDirective", async directiveId => {
  if (!directiveId) return;
  return fetchData(`/finance/directives/cancel/${directiveId}`, "put");
});
export const preCreateDirective = createAsyncThunk("finSettings/preCreateDirective", async ({cofId}) => {
  return fetchData(`/finance/directives/preCreate/${cofId}`, "post");
});

export const completeDirective = createAsyncThunk("finSettings/completeDirective", async ({directiveId}) => {
  if (!directiveId) return;
  return fetchData(`/finance/directives/complete/${directiveId}`, "patch");
});
export const deleteDirective = createAsyncThunk("finSettings/deleteDirective", async ({directiveId}) => {
  if (!directiveId) return;
  return fetchData(`/finance/directives/remove/${directiveId}`, "delete");
});
export const createOrder = createAsyncThunk("finSettings/createOrder", async ({directiveId}) => {
  if (!directiveId) return;
  return fetchData(`/finance/directives/orders/create/${directiveId}`, "post");
});
export const uploadDirectiveDoc = createAsyncThunk(
  "finSettings/uploadDirectiveDoc",
  async ({directiveId, formdata}) => {
    if (!directiveId) return;
    return fetchData(`/finance/directives/docs/upload/${directiveId}`, "post", formdata);
  },
);
export const deleteDirectiveDoc = createAsyncThunk("finSettings/deleteDirectiveDoc", async ({docId}) => {
  if (!docId) return;
  return fetchData(`/finance/directives/docs/remove/${docId}`, "delete");
});
export const fetchDirectiveFilters = createAsyncThunk("finSettings/fetchDirectiveFilters", async params =>
  fetchData(`/finance/directives/getFilters`, "get", {params}),
);
export const fetchExchangeDirectiveFilters = createAsyncThunk(
  "finSettings/fetchExchangeDirectiveFilters",
  async params => fetchDataNew(`/finance/exchanges/filters`, "get", {params}),
);

export const addNewStatementDirective = createAsyncThunk(
  "finSettings/addNewStatementDirective",
  async ({clientId, transactionId, payerAccountId, operationTypeId, coId}) => {
    return fetchData(`/finance/directives/addForStatement`, "post", {
      clientId,
      transactionId,
      payerAccountId,
      operationTypeId,
      coId,
    });
  },
);
export const chooseDirectiveForStatement = createAsyncThunk(
  "finSettings/chooseDirectiveForStatement",
  async ({directiveId, transactionId, payerAccountId, noMessage}, {rejectWithValue}) => {
    return fetchDataNew(`/finance/directives/chooseForStatement`, "put", {
      body: {directiveId, transactionId, payerAccountId},
      noMessage,
      rejectWithValue,
    });
  },
);

//*** /Directives ***

//*** Transactions ***
export const fetchAllTransactions = createAsyncThunk("finSettings/fetchAllTransactions", async ({table}) =>
  fetchData("/finance/transactions/getAll", "get", {params: {table}}),
);
export const addNewTransaction = createAsyncThunk("finSettings/addNewTransaction", async ({orderId, body}) => {
  if (!orderId) return;
  return fetchData(`/finance/transactions/add/${orderId}`, "post", body);
});
//*** /Transactions ***

export const fetchAllCurrency = createAsyncThunk("finSettings/fetchAllCurrency", async () =>
  fetchData("/references/currency", "get"),
);

//*** Fin Orders */
export const fetchFinOrder = createAsyncThunk(
  "finSettings/fetchFinOrder",
  async (orderId, {rejectWithValue, signal}) => {
    if (!orderId) return;
    return fetchDataNew(`/finance/directives/orders/get/${orderId}`, "get", {rejectWithValue, signal});
  },
);
export const addNewFinOrder = createAsyncThunk("finSettings/addNewFinOrder", async ({directiveId, body}) => {
  if (!directiveId) return;
  return fetchDataNew(`/finance/directives/orders/add/${directiveId}`, "post", {body, noMessage: true});
});
export const saveFinOrder = createAsyncThunk("finSettings/saveFinOrder", async ({orderId, note}) => {
  if (!orderId) throw new Error("Order not set");
  return fetchData(`/finance/directives/orders/save/${orderId}`, "patch", {note}, null, false, null, true);
});
export const printFinOrder = createAsyncThunk("finSettings/printFinOrder", async ({orderId}) => {
  if (!orderId) return;
  return fetchData(`/finance/directives/orders/print/${orderId}`, "patch");
});
export const completeFinOrder = createAsyncThunk("finSettings/completeFinOrder", async ({orderId}) => {
  if (!orderId) return;
  return fetchData(`/finance/directives/orders/complete/${orderId}`, "patch");
});
export const deleteFinOrder = createAsyncThunk("finSettings/deleteFinOrder", async ({orderId}) => {
  if (!orderId) return;
  return fetchData(`/finance/directives/orders/remove/${orderId}`, "delete");
});
export const fetchOperationTypes = createAsyncThunk("finSettings/fetchOperationTypes", async ({typeId, fromCo, coId}) =>
  fetchData(`/finance/directives/operationType/getAll`, "get", {params: {typeId, fromCo, coId}}),
);
export const fetchFinOrderTypes = createAsyncThunk("finSettings/fetchFinOrderTypes", async ({operationTypeId}) =>
  fetchData(`/finance/directives/orders/types/getAll`, "get", {params: {operationTypeId}}),
);
export const fetchRepaymentTerms = createAsyncThunk("finSettings/fetchRepaymentTerms", async () =>
  fetchData(`/finance/directives/orders/repaymentTerms/getAll`, "get"),
);

export const fetchAllFinStatuses = createAsyncThunk("finSettings/fetchAllFinStatuses", async () =>
  fetchList(`/finance/directives/status/getAll`, "get"),
);

export const runOrder = createAsyncThunk("finSettings/runOrder", async ({orderId, body}) => {
  return fetchData(`/finance/directives/orders/run/${orderId}`, "patch", body);
});

export const deleteOrder = createAsyncThunk("finSettings/deleteOrder", async orderId =>
  fetchData(`/finance/directives/orders/remove/${orderId}`, "delete"),
);

export const printOrder = createAsyncThunk("finSettings/printOrder", async orderId =>
  fetchData(`finance/directives/orders/print/${orderId}`, "patch"),
);

export const compleateOrder = createAsyncThunk(
  "finSettings/compleateOrder",
  async ({orderId, note}, {rejectWithValue}) =>
    fetchDataNew(`finance/directives/orders/complete/${orderId}`, "patch", {
      rawBody: {note},
      rejectWithValue,
    }),
);

export const uploadCustomCurrencyDoc = createAsyncThunk(
  "finance/uploadCustomCurrencyDoc",
  async ({orderId, data}, {rejectWithValue, signal}) =>
    fetchDataNew(`finance/directives/orders/file/${orderId}`, "post", {
      rawBody: data,
      rejectWithValue,
      signal,
      noMessage: true,
    }),
);
export const removeCustomCurrencyDoc = createAsyncThunk("finance/removeCustomCurrencyDoc", async ({orderId}) =>
  fetchDataNew(`finance/directives/orders/file/${orderId}`, "delete"),
);
//*** Exchange Rates ***
export const fetchAllExchangeRates = createAsyncThunk(
  "finSettings/fetchAllExchangeRates",
  async (params = {}, {rejectWithValue, signal}) =>
    fetchDataNew(`/finance/exchangeRates/getAll`, "get", {params, rejectWithValue, signal}),
);
export const fetchAllEcbRates = createAsyncThunk(
  "finSettings/fetchAllEcbRates",
  async ({page, dateStart, dateEnd, currencyId} = {}) =>
    fetchData(`/finance/exchangeRates/getEcbRates`, "get", {params: {page, dateStart, dateEnd, currencyId}}),
);
export const fetchAllCurrencies = createAsyncThunk("finSettings/fetchAllCurrencies", async () =>
  fetchData(`/finance/exchangeRates/getCurrency`, "get"),
);
export const addNewExchangeRate = createAsyncThunk("finSettings/addNewExchangeRate", async ({currencyId, body}) => {
  if (!currencyId) return;
  return fetchData(`/finance/exchangeRates/add/${currencyId}`, "post", body, null, false, null, true);
});
export const fetchExchangeRate = createAsyncThunk(
  "finSettings/fetchExchangeRate",
  async ({currencyId, orgStructureId} = {}, {rejectWithValue, signal}) =>
    fetchDataNew(`/finance/exchangeRates/get/${currencyId}/${orgStructureId}`, "get", {
      rejectWithValue,
      signal,
      withoutError: true,
    }),
);

//*** Exchange Rates V2***
export const fetchAllExchangeRatesV2 = createAsyncThunk(
  "finSettings/fetchAllExchangeRatesV2",
  async (params = {}, {rejectWithValue, signal}) =>
    fetchDataNew(`/v2/finance/rates`, "get", {params, rejectWithValue, signal}),
);
export const addNewExchangeRateV2 = createAsyncThunk(
  "finSettings/addNewExchangeRateV2",
  async ({currencyId, orgStructureId, saleRate, purchaseRate}) => {
    if (!currencyId) return;
    return fetchDataNew(`/v2/finance/rates`, "post", {
      body: {currencyId, orgStructureId, saleRate, purchaseRate},
      noMessage: true,
    });
  },
);
export const fetchExchangeRateV2 = createAsyncThunk(
  "finSettings/fetchExchangeRateV2",
  async ({currencyId, orgStructureId} = {}, {rejectWithValue, signal}) =>
    fetchDataNew(`/v2/finance/exchangeRates/get/${currencyId}/${orgStructureId}`, "get", {
      rejectWithValue,
      signal,
      withoutError: true,
    }),
);

//Packages

//Packages
export const fetchPackage = createAsyncThunk("finSettings/fetchPackage", async transactionPackageId => {
  if (!transactionPackageId) return;
  return fetchData(`/finance/transactionTemplates/packages/get/${transactionPackageId}`, "get");
});
export const removePackage = createAsyncThunk("finSettings/removePackage", async transactionPackageId => {
  if (!transactionPackageId) return;
  return fetchData(`/finance/transactionTemplates/removeLast/${transactionPackageId}`, "delete");
});

///// External Accounts
export const fetchExternalAccounts = createAsyncThunk(
  "finSettings/fetchExternalAccounts",
  async ({signal, ...filters} = {}) => {
    return fetchListNew(`/finance/externalAccounts/getAll`, "get", {params: filters, signal});
  },
);
export const unlinkExternalAccounts = createAsyncThunk("finSettings/unlinkExternalAccounts", async ({accountId}) => {
  return fetchData(`/finance/externalAccounts/unlink/${accountId}`, "put");
});
export const linkExternalAccounts = createAsyncThunk(
  "finSettings/linkExternalAccounts",
  async ({accountId, payerAccountId}) => {
    return fetchData(`/finance/externalAccounts/link/${accountId}/${payerAccountId}`, "put");
  },
);

//////// creditParams

export const fetchCounterpartyClasses = createAsyncThunk("finSettings/fetchCounterpartyClasses", async () => {
  return fetchList(`finance/creditParams/counterpartyClasses/getAll`);
});

//** Product Groups */
export const fetchAllProductGroups = createAsyncThunk(
  "finSettings/productGroups/fetchAllProductGroups",
  async ({isUsed} = {}) => fetchList(`/finance/productGroup/getAll`, "get", {params: {isUsed}}),
);
export const fetchProductGroup = createAsyncThunk(
  "finSettings/productGroups/fetchProductGroup",
  async productGroupId => {
    if (!productGroupId) return;
    return fetchData(`/finance/productGroup/get/${productGroupId}`, "get");
  },
);
export const addNewProductGroup = createAsyncThunk(
  "finSettings/productGroups/addNewProductGroup",
  async ({nameEng, nameUkr}) => {
    return fetchData(`/finance/productGroup/add`, "post", {nameEng, nameUkr});
  },
);
export const removeProductGroup = createAsyncThunk(
  "finSettings/productGroups/removeProductGroup",
  async productGroupId => {
    if (!productGroupId) return;
    return fetchData(`/finance/productGroup/remove/${productGroupId}`, "delete");
  },
);

//** Invoices */
export const fetchInvoiceFilters = createAsyncThunk(
  "finSettings/invoices/fetchInvoiceFilters",
  async ({invoiceId, orgStructureId, productGroupId} = {}) => {
    return fetchData(`/finance/invoice/getFilters`, "get", {params: {invoiceId, orgStructureId, productGroupId}});
  },
);
export const fetchAllInvoiceStatuses = createAsyncThunk("finSettings/invoices/fetchAllInvoiceStatuses", async () =>
  fetchList(`/finance/invoice/invoiceStatuses/getAll`, "get"),
);
export const fetchAllInvoices = createAsyncThunk("finSettings/invoices/fetchAllInvoices", async filters =>
  fetchList(`/finance/invoice/getAll`, "get", {params: filters}),
);
export const fetchInvoice = createAsyncThunk("finSettings/invoices/fetchInvoice", async invoiceId => {
  if (!invoiceId) return;
  return fetchData(`/finance/invoice/get/${invoiceId}`, "get");
});
export const addNewInvoice = createAsyncThunk("finSettings/invoices/addNewInvoice", async body => {
  return fetchData(`/finance/invoice/add`, "post", body);
});
export const addInvoiceDetails = createAsyncThunk(
  "finSettings/invoices/addInvoiceDetails",
  async ({invoiceId, body}) => {
    return fetchData(`/finance/invoice/addDetails/${invoiceId}`, "post", body);
  },
);
export const updateInvoice = createAsyncThunk("finSettings/invoices/updateInvoice", async ({invoiceId, body}) => {
  if (!invoiceId) return;
  return fetchData(`/finance/invoice/update/${invoiceId}`, "patch", body);
});
export const removeInvoice = createAsyncThunk("finSettings/invoices/removeInvoice", async invoiceId => {
  if (!invoiceId) return;
  return fetchData(`/finance/invoice/remove/${invoiceId}`, "delete");
});

//*** Registers */
export const fetchAllRegisters = createAsyncThunk("finSettings/invoiceRegister/fetchAllRegisters", async filters =>
  fetchList(`/finance/invoiceRegister/getAllRegister`, "get", {params: filters}),
);
export const fetchInvoiceRegister = createAsyncThunk(
  "finSettings/invoiceRegister/fetchInvoiceRegister",
  async registerId => fetchList(`/finance/invoiceRegister/getOne/${registerId}`, "get"),
);
export const fetchInvoiceRegisterFilters = createAsyncThunk(
  "finSettings/invoiceRegister/fetchInvoiceRegisterFilters",
  async (params = {}) => {
    return fetchData(`/finance/invoiceRegister/getFilters`, "get", {params});
  },
);
export const fetchRecipientInfoFromRegister = createAsyncThunk(
  "finSettings/invoiceRegister/fetchRecipientInfoFromRegister",
  async directiveId => {
    return fetchData(`/finance/invoiceRegister/getRecipient/${directiveId}`, "get", null, null, false, null, true);
  },
);
export const fetchAllInvoiceRegisterStatuses = createAsyncThunk(
  "finSettings/invoiceRegister/fetchAllInvoiceRegisterStatuses",
  async () => {
    return fetchData(`/finance/invoiceRegister/getStatuses`, "get");
  },
);
export const addNewInvoiceRegister = createAsyncThunk(
  "finSettings/invoiceRegister/addNewInvoiceRegister",
  async ({directiveId, body}) => {
    if (!directiveId) {
      console.error("no directiveId");
      return;
    }
    return fetchData(`/finance/invoiceRegister/createRegister/${directiveId}`, "post", body);
  },
);
export const uploadRegisterInvoiceDoc = createAsyncThunk(
  "finSettings/invoiceRegister/uploadRegisterInvoiceDoc",
  async ({formdata, registerId, noMessage}, {rejectWithValue}) => {
    if (!registerId) {
      console.error("no registerId");
      return;
    }
    return fetchDataNew(`/finance/invoiceRegister/uploadDoc/${registerId}`, "post", {
      rawBody: formdata,
      noMessage,
      rejectWithValue,
    });
  },
);
export const verificateRegisterInvoice = createAsyncThunk(
  "finSettings/invoiceRegister/verificateRegisterInvoice",
  async registerId => {
    if (!registerId) {
      console.error("no registerId");
      return;
    }
    return fetchData(`/finance/invoiceRegister/verify/${registerId}`, "patch");
  },
);
export const sendInvoiceRegisterToVerification = createAsyncThunk(
  "finSettings/invoiceRegister/sendInvoiceRegisterToVerification",
  async registerId => {
    if (!registerId) {
      console.error("no registerId");
      return;
    }
    return fetchData(`/finance/invoiceRegister/send/${registerId}`, "patch");
  },
);
export const cancelRegisterInvoice = createAsyncThunk(
  "finSettings/invoiceRegister/cancelRegisterInvoice",
  async registerId => {
    if (!registerId) {
      console.error("no registerId");
      return;
    }
    return fetchData(`/finance/invoiceRegister/cancel/${registerId}`, "patch");
  },
);
export const removeRegisterInvoice = createAsyncThunk(
  "finSettings/invoiceRegister/removeRegisterInvoice",
  async registerId => {
    if (!registerId) {
      console.error("no registerId");
      return;
    }
    return fetchData(`/finance/invoiceRegister/removeRegister/${registerId}`, "delete");
  },
);
export const updateInvoiceInRegister = createAsyncThunk(
  "finSettings/invoiceRegister/updateInvoiceInRegister",
  async ({registerId, invoiceId, body, noMessage}, {rejectWithValue}) => {
    if (!registerId) {
      console.error("no registerId");
      return;
    }
    return fetchDataNew(`/finance/invoiceRegister/update/${registerId}`, "patch", {
      rawBody: {invoiceId, ...body},
      noMessage,
      rejectWithValue,
    });
  },
);

//*** Turnovers */
export const fetchAllTurnovers = createAsyncThunk(
  "finSettings/fetchAllTurnovers",
  async (params, {rejectWithValue}) => {
    return fetchDataNew(`/finance/turnovers/getAll`, "get", {params, rejectWithValue});
  },
);
export const fetchTurnoverFilters = createAsyncThunk(
  "finSettings/fetchTurnoverFilters",
  async (params, {rejectWithValue}) => {
    return fetchDataNew(`/finance/turnovers/getFilters`, "get", {params, rejectWithValue});
  },
);

//*** ForeingBank */
export const createForeingBank = createAsyncThunk("finSettings/createForeingBank", async body =>
  fetchDataNew("/finance/banks", "post", {rawBody: body, withoutError: true}),
);
export const updateForeingBank = createAsyncThunk("finSettings/updateForeingBank", async body =>
  fetchData(`/finance/banks/${body.bankMfis}/${body.countryId}`, "patch", body),
);
export const deleteForeingBank = createAsyncThunk("finSettings/deleteForeingBank", async ({bankMfis, countryId}) =>
  fetchData(`/finance/banks/${bankMfis}/${countryId}`, "delete").then(() => ({bankMfis, countryId})),
);
export const fetchAllForeingBanks = createAsyncThunk("finSettings/fetchAllForeingBanks", async params =>
  fetchData("/finance/banks", "get", {params: params}),
);
export const fetchForeingBank = createAsyncThunk("finSettings/fetchForeingBank", async ({bankMfis, countryId}) =>
  fetchData(`/finance/banks/${bankMfis}/${countryId}`, "get"),
);
export const fetchForeingBanksFilters = createAsyncThunk("finSettings/fetchForeingBanksFilters", async params =>
  fetchData(`/finance/banks/getFilters`, "get", {params}),
);

export const FIN_SETTINGS_STORE_NAME = "finSettings";

const FinSettingsSlice = createSlice({
  name: FIN_SETTINGS_STORE_NAME,
  initialState,
  reducers: {
    setCurrentDirectiveOperationTypeId: (state, {payload}) => {
      state.currentDirectiveOperationTypeId = payload;
    },
    clearCurrentBsAccount: state => {
      state.currentBsAccount = null;
    },
    clearCurrentAccount: state => {
      state.currentAccount = null;
    },
    clearDeskOperations: state => {
      state.deskOperations = [];
    },
    clearAccountOwners: state => {
      state.deskOperations = [];
    },
    clearDirectiveDoc: state => {
      state.directiveDoc = null;
    },
    clearDirective: state => {
      state.directive = null;
    },
    clearPackage: state => {
      state.package = null;
    },
    changeDirectivePurpose: (state, {payload, payload: {transactionId, paymentPurpose} = {}}) => {
      if (!payload) return;

      state.directive.transactions = state.directive.transactions.map(transaction => {
        if (transaction.transactionId === transactionId) {
          transaction.paymentPurpose = paymentPurpose;
        }
        return transaction;
      });
    },
    clearFinOrderTypes: state => {
      state.finOrderTypes = [];
    },
    clearOperationTypes: state => {
      state.operationTypes = [];
    },
    setCreatedDirective: (state, {payload}) => {
      state.createdDirective = payload;
    },
    clearCreatedDirective: state => {
      state.createdDirective = null;
    },
    setStatementDirective: (state, {payload}) => {
      state.statementDirective = payload;
    },
    clearStatementDirective: state => {
      state.statementDirective = null;
    },
    setFinOrder: (state, {payload}) => {
      state.finOrder = payload;
    },
    clearFinOrder: state => {
      state.finOrder = null;
    },
    clearInvoiceFiltersData: state => {
      state.invoiceFiltersData = {};
    },
    clearInvoiceRegister: state => {
      state.register = null;
    },
    clearInvoices: state => {
      state.invoices = null;
    },
    clearInvoice: state => {
      state.invoice = null;
    },
    clearCurrentRate: state => {
      state.currentRate = null;
    },
  },
  extraReducers: builder => {
    builder
      //** Balance Sheet Accounts **
      .addCase(fetchAllBsaTypes.fulfilled, (state, {payload}) => {
        state.types = payload;
      })
      .addCase(fetchOwnerTypes.fulfilled, (state, {payload}) => {
        state.ownerTypes = payload;
      })
      .addCase(fetchAllBsaClasses.fulfilled, (state, {payload}) => {
        state.classes = payload;
      })
      .addCase(fetchAllBsAccounts.fulfilled, (state, {payload}) => {
        state.bsAccounts = payload;
      })
      .addCase(fetchBsAccount.fulfilled, (state, {payload}) => {
        state.currentBsAccount = payload;
      })
      .addCase(addNewBsAccount.fulfilled, (state, {payload}) => {
        state.currentBsAccount = payload;
      })
      .addCase(updateBsAccount.fulfilled, (state, {payload}) => {
        state.currentBsAccount = payload;
      })
      .addCase(deleteBsAccount.fulfilled, state => {
        state.currentBsAccount = null;
      })
      //** Accounts **
      .addCase(fetchAllAccounts.fulfilled, (state, {payload}) => {
        if (!payload) return;

        if (payload.currentPage > 1) {
          state.accounts = [...state.accounts, ...payload.accounts];
        } else {
          state.accounts = payload.accounts;
        }
      })
      .addCase(fetchAccount.fulfilled, (state, {payload}) => {
        state.currentAccount = payload;
      })
      .addCase(addNewAccount.fulfilled, (state, {payload}) => {
        if (!payload) return;

        state.currentAccount = payload.account;
      })
      .addCase(saveAccount.fulfilled, (state, {payload}) => {
        if (!payload) return;

        state.currentAccount = payload.account;
      })
      .addCase(deleteAccount.fulfilled, state => {
        state.currentAccount = null;
      })

      //*** Transaction templates ***
      .addCase(fetchTransactionPackageGroups.fulfilled, (state, {payload}) => {
        state.transactionPackageGroups = payload;
      })

      //*** Desk Operations ***
      .addCase(fetchAllDeskOperations.fulfilled, (state, {payload}) => {
        state.deskOperations = payload;
      })
      .addCase(runOperation.fulfilled, (state, {payload}) => {
        state.directive = payload;
      })
      .addCase(fetchOperation.fulfilled, (state, {payload}) => {
        state.currentOperation = payload;
      })

      //*** Transactions ***
      .addCase(fetchAllTransactions.fulfilled, (state, {payload}) => {
        state.transactions = payload;
      })
      .addCase(addNewTransaction.fulfilled, (state, {payload}) => {
        if (!payload) return;

        // state.transactions = payload.order.transactions;
        state.finOrder = payload.order;
      })

      //*** Directives ***
      .addCase(fetchAllDirectives.fulfilled, (state, {payload}) => {
        if (!payload?.data) return;
        state.allDirectives = payload.currentPage > 1 ? [...state.allDirectives, ...payload.data] : payload.data;
      })
      .addCase(fetchDirective.fulfilled, (state, {payload}) => {
        state.directive = payload;
      })
      .addCase(fetchStatementDirective.fulfilled, (state, {payload}) => {
        state.statementDirective = payload;
      })
      .addCase(saveDirective.fulfilled, (state, {payload}) => {
        state.directive = payload;
      })
      .addCase(addNewDirective.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.createdDirective = payload.directive;
      })
      .addCase(updateDirective.fulfilled, (state, {payload}) => {
        if (!payload) return;

        state.directive = payload.directive;
      })
      .addCase(cancelDirective.fulfilled, state => {
        state.directive = null;
      })
      .addCase(completeDirective.fulfilled, (state, {payload}) => {
        state.directive = payload;
      })
      .addCase(deleteDirective.fulfilled, state => {
        state.directive = null;
      })
      .addCase(uploadDirectiveDoc.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.directiveDoc = payload.docs[0].files;
      })
      .addCase(deleteDirectiveDoc.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.directiveDoc = payload.docs;
      })
      .addCase(fetchDirectiveFilters.fulfilled, (state, {payload}) => {
        state.filters = payload;
      })
      .addCase(fetchExchangeDirectiveFilters.fulfilled, (state, {payload}) => {
        state.filters = payload;
      })

      .addCase(fetchAllCurrency.fulfilled, (state, {payload}) => {
        state.allCurrency = payload;
      })
      .addCase(preCreateDirective.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.readyToCreateDirectiveCO = payload?.co ?? {};
      })

      .addCase(addNewStatementDirective.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.statementDirective = payload.directive;
      })
      .addCase(chooseDirectiveForStatement.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.statementDirective = payload.directive;
      })

      //*** Fin Orders */
      .addCase(fetchFinOrder.fulfilled, (state, {payload}) => {
        state.finOrder = payload;
      })
      .addCase(addNewFinOrder.fulfilled, (state, {payload}) => {
        if (!payload) return;

        if (payload?.directive) {
          state.statementDirective = payload.directive;
          state.directive = payload.directive;
          state.directives = state.directives?.map(directive => {
            return directive?.directiveId === payload?.directive?.directiveId ? payload.directive : directive;
          });
        }
        if (payload?.order) {
          state.finOrder = payload.order;
        }
      })
      .addCase(addNewFinOrder.rejected, (state, {error}) => {
        toaster.warning(error?.message || "no credit account", 2500);
      })
      .addCase(saveFinOrder.fulfilled, (state, {payload}) => {
        if (!payload) return;
        if (payload?.order) {
          state.finOrder = payload.order;
          toaster.success(t("The Order filled and saved", {orderNumber: payload.order.orderNumber}));
        }
      })
      .addCase(printFinOrder.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.finOrder = payload.order;
      })
      .addCase(completeFinOrder.fulfilled, (state, {payload}) => {
        state.finOrder = payload;
      })
      .addCase(deleteFinOrder.fulfilled, state => {
        state.finOrder = null;
      })
      .addCase(fetchOperationTypes.fulfilled, (state, {payload}) => {
        state.operationTypes = payload;
      })
      .addCase(fetchFinOrderTypes.fulfilled, (state, {payload}) => {
        state.finOrderTypes = payload;
      })
      .addCase(fetchRepaymentTerms.fulfilled, (state, {payload}) => {
        state.repaymentTerms = payload;
      })

      .addCase(fetchAllFinStatuses.fulfilled, (state, {payload}) => {
        state.statuses = payload;
      })

      //*** Exchange Rates ***
      .addCase(fetchAllExchangeRates.pending, state => {
        state.allExchangeRatesLoadingStatus = true;
      })
      .addCase(fetchAllExchangeRates.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.allExchangeRates =
          payload.currentPage === 1 ? payload.rates : [...state.allExchangeRates, ...payload.rates];
        state.allExchangeRatesLoadingStatus = false;
      })
      .addCase(fetchAllExchangeRates.rejected, state => {
        state.allExchangeRatesLoadingStatus = false;
      })
      .addCase(fetchAllExchangeRatesV2.pending, state => {
        state.allExchangeRatesLoadingStatus = true;
      })
      .addCase(fetchAllExchangeRatesV2.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.allExchangeRates =
          payload.currentPage === 1 ? payload.rates : [...state.allExchangeRates, ...payload.rates];
        state.allExchangeRatesLoadingStatus = false;
      })
      .addCase(fetchAllExchangeRatesV2.rejected, state => {
        state.allExchangeRatesLoadingStatus = false;
      })
      .addCase(fetchAllEcbRates.pending, state => {
        state.allEcbRatesLoadingStatus = true;
      })
      .addCase(fetchAllEcbRates.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.allEcbRates = payload.currentPage === 1 ? payload.rates : [...state.allEcbRates, ...payload.rates];
        state.allEcbRatesLoadingStatus = false;
      })
      .addCase(fetchAllEcbRates.rejected, state => {
        state.allEcbRatesLoadingStatus = false;
      })
      .addCase(fetchAllCurrencies.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.allCurrency = [{shortName: "EUR", currencyId: 978}, ...payload.currencies];
      })
      .addCase(addNewExchangeRate.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.allExchangeRates = [payload.rate, ...state.allExchangeRates];
      })
      .addCase(addNewExchangeRateV2.fulfilled, (state, {payload}) => {
        if (!payload) return;
        if (state.allExchangeRates.find(rate => rate.exchangeRateId === payload.rate.exchangeRateId)) {
          state.allExchangeRates = state.allExchangeRates.map(rate =>
            rate.exchangeRateId === payload.rate.exchangeRateId ? payload.rate : rate,
          );
        } else {
          state.allExchangeRates = [payload.rate, ...state.allExchangeRates];
        }
      })
      .addCase(fetchExchangeRate.fulfilled, (state, {payload}) => {
        state.currentRate = payload;
      })
      .addCase(fetchExchangeRate.rejected, state => {
        state.currentRate = null;
      })

      .addCase(runOrder.fulfilled, (state, {payload}) => {
        if (!payload?.order) return;
        state.finOrder = payload.order;
      })
      .addCase(uploadCustomCurrencyDoc.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.finOrder.file = payload.data;
      })
      .addCase(removeCustomCurrencyDoc.fulfilled, state => {
        state.finOrder.file = null;
      })
      .addCase(compleateOrder.fulfilled, (state, {payload}) => {
        if (payload?.order) {
          state.finOrder = payload?.order;
          toaster.success(t("Order compleated"));
        }
      })
      .addCase(printOrder.fulfilled, () => {})
      .addCase(deleteOrder.fulfilled, (state, {meta}) => {
        state.finOrder = null;
        state.directive = {
          ...state.directive,
          orders: state.directive?.orders?.filter(order => order.orderId !== meta.arg),
        };
        state.statementDirective = {
          ...state.statementDirective,
          orders: state.statementDirective?.orders?.filter(order => order.orderId !== meta.arg),
        };
        toaster.success(t("Order deleted"));
      })

      //Packages
      .addCase(fetchPackage.fulfilled, (state, {payload}) => {
        state.package = payload;
      })
      .addCase(removePackage.fulfilled, (state, {payload}) => {
        state.package = payload;
      })

      /// External Accounts
      .addCase(fetchExternalAccounts.fulfilled, (state, {payload}) => {
        state.externalAccounts = payload;
      })
      .addCase(fetchExternalAccountFilters.fulfilled, (state, {payload}) => {
        state.externalAccountFilters = payload;
      })

      // Banks and External Accounts ForeignCompany!
      .addCase(fetchBanks.pending, state => {
        state.banksLoading = true;
      })
      .addCase(fetchBanks.fulfilled, (state, {payload}) => {
        state.banks = payload;
        state.banksLoading = false;
      })
      .addCase(fetchBanks.rejected, state => {
        state.banks = [];
        state.banksLoading = false;
      })
      .addCase(fetchExternalAccount.fulfilled, (state, {payload}) => {
        state.currentExternalAccount = payload || null;
      })

      // ?Companies
      .addCase(fetchCompanies.pending, state => {
        state.companiesLoading = true;
      })
      .addCase(fetchCompanies.fulfilled, (state, {payload}) => {
        state.companies = payload || [];
        state.companiesLoading = false;
      })
      .addCase(fetchCompanies.rejected, state => {
        state.companies = [];
        state.companiesLoading = false;
      })
      .addCase(fetchCompany.fulfilled, (state, {payload}) => {
        state.currentCompany = payload || null;
      })
      .addCase(createCompany.fulfilled, (state, {payload}) => {
        if (!payload) return;

        if (payload) state.companies = [payload, ...state.companies];
      })
      .addCase(updateCompany.fulfilled, (state, {payload, meta}) => {
        if (!payload) return;

        const id = meta?.arg?.companyId;
        if (id && payload) {
          state.companies = state.companies.map(c => (c.companyId === id ? {...c, ...payload} : c));
          if (state.currentCompany?.companyId === id) state.currentCompany = {...state.currentCompany, ...payload};
        }
      })
      .addCase(deleteCompany.fulfilled, (state, {meta}) => {
        const id = meta?.arg;
        if (id) {
          state.companies = state.companies.filter(c => c.companyId !== id);
          if (state.currentCompany?.companyId === id) state.currentCompany = null;
        }
      })
      .addCase(fetchCompanyFilters.fulfilled, (state, {payload}) => {
        state.companyFilters = payload || {};
      })
      .addCase(fetchCompanyTypes.fulfilled, (state, {payload}) => {
        state.companyTypes = payload || [];
      })
      .addCase(fetchCompanyStatuses.fulfilled, (state, {payload}) => {
        state.companyStatuses = payload || [];
      })
      // .addCase(createExternalAccount.fulfilled, (_state, {_payload}) => {
      //   // On create, rely on refetch from UI; keep reducer minimal
      // })
      // .addCase(updateExternalAccount.fulfilled, (_state, {_payload}) => {
      //   // On update, rely on refetch from UI
      // })
      .addCase(deleteExternalAccount.fulfilled, (state, {meta}) => {
        if (state.currentExternalAccount?.externalAccountId === meta.arg) {
          state.currentExternalAccount = null;
        }
      })
      /////

      .addCase(fetchCounterpartyClasses.fulfilled, (state, {payload}) => {
        state.counterpartyClasses = payload;
      })

      //** Product Groups */
      .addCase(fetchAllProductGroups.fulfilled, (state, {payload}) => {
        state.productGroups = payload;
      })
      .addCase(fetchProductGroup.fulfilled, (state, {payload}) => {
        state.productGroup = payload;
      })
      .addCase(addNewProductGroup.fulfilled, (state, {payload}) => {
        if (!payload) return;

        // state.productGroup = payload;
        state.productGroups = [...state.productGroups, payload];
      })
      .addCase(removeProductGroup.fulfilled, (state, {meta}) => {
        // state.productGroup = null;
        state.productGroups = state.productGroups.filter(({productGroupId}) => productGroupId !== meta.arg);
      })

      //** Invoices */
      .addCase(fetchInvoiceFilters.fulfilled, (state, {payload}) => {
        state.invoiceFiltersData = payload;
      })
      .addCase(fetchAllInvoiceStatuses.fulfilled, (state, {payload}) => {
        state.invoiceStatuses = payload;
      })
      .addCase(fetchAllInvoices.fulfilled, (state, {payload}) => {
        state.invoices = payload;
      })
      .addCase(fetchInvoice.fulfilled, (state, {payload}) => {
        state.invoice = payload;
      })
      .addCase(addNewInvoice.fulfilled, (state, {payload}) => {
        if (!payload) return;

        // state.invoice = payload;
        state.invoices = [payload, ...state.invoices];
      })
      .addCase(updateInvoice.fulfilled, (state, {payload, meta}) => {
        if (!payload) return;

        // state.invoice = payload;
        state.invoices = state.invoices.map(invoice => {
          if (invoice.invoiceId === meta.arg.invoiceId) {
            return {...invoice, ...payload};
          }
          return invoice;
        });
      })
      .addCase(removeInvoice.fulfilled, (state, {meta}) => {
        // state.invoice = payload;
        state.invoices = state.invoices.filter(({invoiceId}) => invoiceId !== meta.arg);
      })

      //*** Registers */
      .addCase(fetchAllRegisters.fulfilled, (state, {payload}) => {
        if (payload) {
          state.registers = payload.currentPage === 1 ? payload.data : [...state.registers, ...payload.data];
        }
      })
      .addCase(fetchInvoiceRegister.fulfilled, (state, {payload}) => {
        state.register = payload;
      })
      .addCase(addNewInvoiceRegister.fulfilled, (state, {meta, payload}) => {
        if (!payload?.register) return;
        state.register = payload.register;
        // state.allDirectives = state.allDirectives.map(directive => {
        //   if (directive.directiveId === meta.arg.directiveId) {
        //     directive.register = {
        //       registerId: payload.invoiceRegister.registerId,
        //       statusId: payload.invoiceRegister.statusId,
        //     };
        //   }
        //   return directive;
        // });
        toaster.info("Рахунок-фактору збережено");
      })
      .addCase(cancelRegisterInvoice.fulfilled, (state, {payload}) => {
        state.register = payload;
      })
      .addCase(removeRegisterInvoice.fulfilled, (state, {meta}) => {
        state.register = null;
        // state.allDirectives = state.allDirectives.map(directive => {
        //   if (directive.directiveId === meta.arg) {
        //     directive.register = {};
        //   }
        //   return directive;
        // });
      })
      // .addCase(sendInvoiceRegisterToVerification.fulfilled, (state, {meta}) => {
      //   state.allDirectives = state.allDirectives.map(directive => {
      //     if (directive.directiveId === meta.arg) {
      //       directive.register = {...directive.register, statusId: 2};
      //     }
      //     return directive;
      //   });
      // })
      .addCase(fetchInvoiceRegisterFilters.fulfilled, (state, {payload}) => {
        state.invoiceRegisterFiltersData = payload;
      })
      .addCase(updateInvoiceInRegister.fulfilled, (state, {payload}) => {
        if (!payload) return;

        state.register = {...state.register, ...(payload?.register ?? {})};
      })
      .addCase(verificateRegisterInvoice.fulfilled, (state, {payload}) => {
        if (!payload) return;

        state.register = {...state.register, ...payload.register};
      })

      //*** Turnovers */
      .addCase(fetchAllTurnovers.fulfilled, (state, {payload: {data, currentPage} = {}}) => {
        state.daylistTransactions = currentPage === 1 ? data : [...state.daylistTransactions, ...data];
      })
      .addCase(fetchTurnoverFilters.fulfilled, (state, {payload}) => {
        state.daylistTransactionFilters = payload;
      })
      //*** ForeingBank */
      .addCase(createForeingBank.fulfilled, (state, {payload}) => {
        if (!payload) return;

        state.foreingBanks = [payload, ...state.foreingBanks];
      })
      .addCase(updateForeingBank.fulfilled, (state, {payload}) => {
        if (!payload) return;

        state.foreingBanks = [payload, ...state.foreingBanks.filter(bank => bank.bankMfis !== payload.bankMfis)];
      })
      .addCase(deleteForeingBank.fulfilled, (state, {payload}) => {
        if (!payload) return;

        state.foreingBanks = state.foreingBanks.filter(
          bank => bank.bankMfis !== payload.bankMfis && bank.countryId !== payload.countryId,
        );
      })
      .addCase(fetchAllForeingBanks.fulfilled, (state, {payload}) => {
        if (!payload) return;

        state.foreingBanks = payload.currentPage > 1 ? [...state.foreingBanks, ...payload.data] : payload.data;
      })
      .addCase(fetchForeingBank.fulfilled, (state, {payload}) => {
        state.foreingBanks = payload;
      })
      .addCase(fetchForeingBanksFilters.fulfilled, (state, {payload}) => {
        state.foreingBanksFilters = payload;
      })

      //fulfilled matcher
      .addMatcher(
        action => action.type.startsWith(FIN_SETTINGS_STORE_NAME) && action.type.endsWith("/fulfilled"),
        state => handleFulfilled(state),
      )
      //pending matcher
      .addMatcher(
        action => action.type.startsWith(FIN_SETTINGS_STORE_NAME) && action.type.endsWith("/pending"),
        state => handlePending(state),
      )
      //rejected matcher
      .addMatcher(
        action => action.type.startsWith(FIN_SETTINGS_STORE_NAME) && action.type.endsWith("/rejected"),
        (state, {error}) => handleRejected(state, error),
      )
      //default
      .addDefaultCase(() => {});
  },
});

function handleFulfilled(state) {
  state.finSettingsLoading = false;
  state.error = false;
}

function handlePending(state) {
  state.finSettingsLoading = true;
  state.error = false;
}

function handleRejected(state, error) {
  state.finSettingsLoading = false;
  state.error = error;
}

const {actions, reducer} = FinSettingsSlice;

export const {
  clearCurrentBsAccount,
  clearCurrentAccount,
  clearDeskOperations,
  clearDirectiveDoc,
  changeDirectivePurpose,
  clearAccountOwners,
  clearDirective,
  clearFinOrderTypes,
  clearOperationTypes,
  clearCreatedDirective,
  setCreatedDirective,
  setFinOrder,
  clearPackage,
  clearStatementDirective,
  clearInvoiceFiltersData,
  clearInvoiceRegister,
  clearInvoices,
  clearInvoice,
  clearCurrentRate,
  clearFinOrder,
  setStatementDirective,
  setCurrentDirectiveOperationTypeId,
} = actions;

export default reducer;

// *** Selectors ***
export const selectBsaTypes = state => state[FIN_SETTINGS_STORE_NAME].types;
export const selectOwnerTypes = state => state[FIN_SETTINGS_STORE_NAME].ownerTypes;
export const selectBsaClasses = state => state[FIN_SETTINGS_STORE_NAME].classes;
export const selectAllBsAccounts = state => state[FIN_SETTINGS_STORE_NAME].bsAccounts;
export const selectBsCurrentAccount = state => state[FIN_SETTINGS_STORE_NAME].currentBsAccount;
export const selectAllAccounts = state => state[FIN_SETTINGS_STORE_NAME].accounts;
export const selectCurrentAccount = state => state[FIN_SETTINGS_STORE_NAME].currentAccount;
export const selectFinSettingsLoading = state => state[FIN_SETTINGS_STORE_NAME].finSettingsLoading;
export const selectTransactionPackageGroups = state => state[FIN_SETTINGS_STORE_NAME].transactionPackageGroups;
export const selectAllDeskOperations = state => state[FIN_SETTINGS_STORE_NAME].deskOperations;
export const selectCurrentOperation = state => state[FIN_SETTINGS_STORE_NAME].currentOperation;
export const selectAllTransactions = state => state[FIN_SETTINGS_STORE_NAME].transactions;
export const selectDirective = state => state[FIN_SETTINGS_STORE_NAME].directive;
export const selectDirectiveDoc = state => state[FIN_SETTINGS_STORE_NAME].directiveDoc;
export const selectAllCurrency = state => state[FIN_SETTINGS_STORE_NAME].allCurrency;
export const readyToCreateDirectiveCO = state => state[FIN_SETTINGS_STORE_NAME].readyToCreateDirectiveCO;
export const selectFinOrder = state => state[FIN_SETTINGS_STORE_NAME].finOrder;
export const selectReadyToCreateDirectiveCO = state => state[FIN_SETTINGS_STORE_NAME].readyToCreateDirectiveCO;
export const selectOperationTypes = state => state[FIN_SETTINGS_STORE_NAME].operationTypes;
export const selectFinOrderTypes = state => state[FIN_SETTINGS_STORE_NAME].finOrderTypes;
export const selectRepaymentTerms = state => state[FIN_SETTINGS_STORE_NAME].repaymentTerms;
export const selectCreatedDirective = state => state[FIN_SETTINGS_STORE_NAME].createdDirective;
export const selectAllStatuses = state => state[FIN_SETTINGS_STORE_NAME].statuses;
export const selectDirectiveFilters = state => state[FIN_SETTINGS_STORE_NAME].filters;
export const selectAllDirectives = state => state[FIN_SETTINGS_STORE_NAME].allDirectives;
export const selectAllExchangeRates = state => state[FIN_SETTINGS_STORE_NAME].allExchangeRates;
export const selectAllEcbRates = state => state[FIN_SETTINGS_STORE_NAME].allEcbRates;
export const selectCurrentRate = state => state[FIN_SETTINGS_STORE_NAME].currentRate;
export const selectAreAllExchangeRatesLoading = state => state[FIN_SETTINGS_STORE_NAME].allExchangeRatesLoadingStatus;
export const selectAreEcbRatesLoading = state => state[FIN_SETTINGS_STORE_NAME].allEcbRatesLoadingStatus;
export const selectPackage = state => state[FIN_SETTINGS_STORE_NAME].package;

export const selectCurrentDirectiveOperationTypeId = state =>
  state[FIN_SETTINGS_STORE_NAME].currentDirectiveOperationTypeId;

export const selectStatementDirective = state => state[FIN_SETTINGS_STORE_NAME].statementDirective;

export const selectAllProductGroups = state => state[FIN_SETTINGS_STORE_NAME].productGroups;
export const selectProductGroup = state => state[FIN_SETTINGS_STORE_NAME].productGroup;

export const selectInvoiceFiltersData = state => state[FIN_SETTINGS_STORE_NAME].invoiceFiltersData;
export const selectInvoiceStatuses = state => state[FIN_SETTINGS_STORE_NAME].invoiceStatuses;
export const selectAllInvoices = state => state[FIN_SETTINGS_STORE_NAME].invoices;
export const selectInvoice = state => state[FIN_SETTINGS_STORE_NAME].invoice;

export const selectInvoiceRegisterFilters = state => state[FIN_SETTINGS_STORE_NAME].invoiceRegisterFiltersData;

export const selectAllRegisters = state => state[FIN_SETTINGS_STORE_NAME].registers;
export const selectRegister = state => state[FIN_SETTINGS_STORE_NAME].register;
export const selectRegisterStatuses = state => state[FIN_SETTINGS_STORE_NAME].registerStatuses;

export const selectDaylistTransactions = state => state[FIN_SETTINGS_STORE_NAME].daylistTransactions;
export const selectDaylistTransactionFilters = state => state[FIN_SETTINGS_STORE_NAME].daylistTransactionFilters;

export const selectExternalAccounts = state => state.finSettings.externalAccounts;
export const selectExternalAccountFilters = state => state[FIN_SETTINGS_STORE_NAME].externalAccountFilters;

export const selectCounterpartyClasses = state => state.finSettings.counterpartyClasses;

export const selectBanks = state => state[FIN_SETTINGS_STORE_NAME].banks;
export const selectBanksLoading = state => state[FIN_SETTINGS_STORE_NAME].banksLoading;
export const selectCompanyAccounts = state => state[FIN_SETTINGS_STORE_NAME].companyAccounts;
export const selectCurrentExternalAccount = state => state[FIN_SETTINGS_STORE_NAME].currentExternalAccount;
export const selectCompanies = state => state[FIN_SETTINGS_STORE_NAME].companies;
export const selectCompaniesLoading = state => state[FIN_SETTINGS_STORE_NAME].companiesLoading;
export const selectCompany = state => state[FIN_SETTINGS_STORE_NAME].currentCompany;
export const selectCompanyFilters = state => state[FIN_SETTINGS_STORE_NAME].companyFilters;
export const selectCompanyTypes = state => state[FIN_SETTINGS_STORE_NAME].companyTypes;
export const selectCompanyStatuses = state => state[FIN_SETTINGS_STORE_NAME].companyStatuses;
export const selectForeingBanks = state => state[FIN_SETTINGS_STORE_NAME].foreingBanks;
export const selectForeingBanksFilters = state => state[FIN_SETTINGS_STORE_NAME].foreingBanksFilters;
